/**
 * Onboarding — activation wizard.
 *
 * Steps: 0=Organisation, 1=ConnectGitHub, 2=SelectRepo, 3=Scan, 4=Agent setup
 *
 * State persists to Convex so users can resume after OAuth redirect,
 * tab close, or a scan handoff.
 * Org + default project are created at step 0 Continue.
 */
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { FragmentsLogo } from '@fragments-sdk/ui';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useCreateOrgForm } from '@/hooks/useCreateOrgForm';
import { ACTIVE_ORG_STORAGE_KEY } from '@/hooks/useOrgContext';
import { useGitHubConnect } from '@/components/github/useGitHubConnect';
import {
  getPreviousOnboardingStep,
  resolveResumeOnboardingStep,
} from '@/lib/onboarding-steps';
import { buildDashboardPath } from '@/lib/routes';
import {
  StepOrganisation,
  StepConnectGitHub,
  StepSelectRepo,
  StepScan,
  StepReady,
  OnboardingErrorBanner,
  OnboardingShell,
} from '@/components/onboarding';
import type {
  OnboardingCompletionPath,
  ScanCompletion,
} from '@/components/onboarding';
import s from '@/page/onboarding/components/onboarding.module.scss';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function githubErrorMessage(code: string): string {
  switch (code) {
    case 'github_connection_not_configured':
      return 'GitHub is not configured for this local environment. Add the GitHub App ID, private key, client ID, client secret, slug, and internal API secret before installing the App.';
    case 'github_callback_missing_params':
      return 'GitHub did not return the authorization details Fragments expected. Try connecting again.';
    case 'github_callback_bad_installation_id':
      return 'GitHub returned an invalid installation ID. Try connecting again.';
    case 'github_invalid_state':
      return 'This GitHub connection session expired or was already used. Start the connection again.';
    case 'github_installation_not_visible':
      return 'GitHub installed the App, but this account cannot verify that installation. Check the selected GitHub account and repository access.';
    default:
      return 'GitHub connection failed. Check the local server logs and try connecting again.';
  }
}

// ── Main ────────────────────────────────────────────────────────

function OnboardingPage() {
  const { user, isLoaded } = useClerkUser();
  const navigate = useNavigate();
  const clerkUserId = user?.id;

  // Convex queries/mutations
  const onboarding = useQuery(
    api.onboarding.getByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );
  const memberships = useQuery(
    api.memberships.listByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );
  const startOnboarding = useMutation(api.onboarding.start);
  const advanceStep = useMutation(api.onboarding.advanceStep);
  const completeOnboarding = useMutation(api.onboarding.complete);

  // Local state
  const [step, setStep] = React.useState(0);
  const [selectedRepo, setSelectedRepo] = React.useState<string | null>(null);
  const [orgId, setOrgId] = React.useState<Id<'orgs'> | null>(null);
  const [projectId, setProjectId] = React.useState<Id<'projects'> | null>(null);
  const [mutationError, setMutationError] = React.useState<string | null>(null);
  const [pendingRetry, setPendingRetry] = React.useState<
    (() => Promise<void>) | null
  >(null);
  const existingOrg = useQuery(api.orgs.getById, orgId ? { orgId } : 'skip');
  const prefetchedOrgNameRef = React.useRef<Id<'orgs'> | null>(null);
  const repoSelectionPersistRef = React.useRef<Promise<boolean> | null>(null);

  // Org creation form
  const orgForm = useCreateOrgForm();

  // GitHub connection
  const github = useGitHubConnect({
    orgId: orgId ?? undefined,
    projectId: projectId ?? undefined,
    source: 'onboarding',
  });

  React.useEffect(() => {
    if (!isLoaded || user) return;
    navigate({ to: '/sign-in', replace: true });
  }, [isLoaded, user, navigate]);

  React.useEffect(() => {
    const url = new URL(window.location.href);
    const githubError = url.searchParams.get('github_error');
    if (!githubError) return;

    setMutationError(githubErrorMessage(githubError));
    setPendingRetry(null);
    url.searchParams.delete('github_error');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }, []);

  // Resume from Convex on mount
  const resumedRef = React.useRef(false);
  React.useEffect(() => {
    if (!onboarding || resumedRef.current) return;
    resumedRef.current = true;

    if (onboarding.completedAt) {
      navigate({ to: '/', replace: true });
      return;
    }

    if (onboarding.orgId) setOrgId(onboarding.orgId);
    if (onboarding.defaultProjectId) setProjectId(onboarding.defaultProjectId);
    if (onboarding.selectedRepo) setSelectedRepo(onboarding.selectedRepo);

    setStep(
      resolveResumeOnboardingStep({
        currentStep: onboarding.currentStep,
        orgId: onboarding.orgId,
        defaultProjectId: onboarding.defaultProjectId,
        selectedRepo: onboarding.selectedRepo,
        scanCompleted: onboarding.scanCompleted,
      }),
    );
  }, [onboarding, navigate]);

  React.useEffect(() => {
    if (
      !isLoaded ||
      !user ||
      onboarding !== null ||
      onboarding === undefined ||
      memberships === undefined
    ) {
      return;
    }

    if (memberships.length > 0) {
      navigate({ to: '/', replace: true });
    }
  }, [isLoaded, user, onboarding, memberships, navigate]);

  React.useEffect(() => {
    if (!existingOrg || prefetchedOrgNameRef.current === existingOrg._id) {
      return;
    }

    prefetchedOrgNameRef.current = existingOrg._id;
    if (orgForm.name.trim()) return;
    orgForm.setName(existingOrg.name);
  }, [existingOrg, orgForm]);

  // The install flow returns with both user auth and an org installation.
  // The existing-installation flow may only create user auth; the repo picker
  // can then list repositories from GitHub's per-user installation API.
  React.useEffect(() => {
    if (
      step === 1 &&
      github.isConnected &&
      !github.isCheckingApp &&
      orgId &&
      clerkUserId
    ) {
      void (async () => {
        const ok = await runStep({ clerkUserId, step: 2 });
        if (ok) setStep(2);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, github.isConnected, github.isCheckingApp, orgId]);

  async function completeAndNavigate(args: {
    clerkUserId: string;
    orgSlug: string | null;
    completionPath?: OnboardingCompletionPath;
  }): Promise<void> {
    try {
      await completeOnboarding({
        clerkUserId: args.clerkUserId,
        completionPath: args.completionPath ?? 'code',
      });
      const destination = args.orgSlug
        ? buildDashboardPath(args.orgSlug)
        : '/';
      navigate({ to: destination as any, replace: true });
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : String(err));
      setPendingRetry(() => async () => {
        setMutationError(null);
        setPendingRetry(null);
        await completeAndNavigate(args);
      });
    }
  }

  // Auto-start onboarding record
  React.useEffect(() => {
    if (!clerkUserId || onboarding !== null) return;
    if (onboarding === undefined || memberships === undefined) return;
    if (memberships.length > 0) return;
    void startOnboarding({ clerkUserId }).catch((err) => {
      setMutationError(err instanceof Error ? err.message : String(err));
      setPendingRetry(() => async () => {
        setMutationError(null);
        setPendingRetry(null);
        await startOnboarding({ clerkUserId });
      });
    });
  }, [clerkUserId, onboarding, memberships, startOnboarding]);

  // ── Step navigation ───────────────────────────────────────────

  // Every wizard transition routes through `runStep`. It traps mutation
  // failures into the recovery-banner state so the user can retry, and
  // records the whole transition so the Retry button re-dispatches both the
  // mutation and the UI/redirect side effect. Returns true on success so call
  // sites can short-circuit.
  async function runStep(
    args: Parameters<typeof advanceStep>[0],
    onSuccess?: () => void | Promise<void>,
    options?: { silent?: boolean },
  ): Promise<boolean> {
    try {
      await advanceStep(args);
      setMutationError(null);
      setPendingRetry(null);
      await onSuccess?.();
      return true;
    } catch (err) {
      // Silent transitions let the calling step own its own contextual error
      // UI (e.g. StepScan's handoff-failed panel) so one failure never shows
      // both the global banner and an in-step error.
      if (!options?.silent) {
        setMutationError(err instanceof Error ? err.message : String(err));
        setPendingRetry(() => async () => {
          await runStep(args, onSuccess, options);
        });
      }
      return false;
    }
  }

  async function handleOrgContinue() {
    if (!clerkUserId) return;

    if (orgId && projectId) {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
      await runStep(
        {
          clerkUserId,
          step: 1,
          scanCompleted: false,
          orgId,
          defaultProjectId: projectId,
        },
        () => setStep(1),
      );
      return;
    }

    const result = await orgForm.submit();
    if (!result) return;

    const newOrgId = result.org._id;
    const newProjectId = result.defaultProject._id;
    setOrgId(newOrgId);
    setProjectId(newProjectId);
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, newOrgId);

    await runStep(
      {
        clerkUserId,
        step: 1,
        scanCompleted: false,
        orgId: newOrgId,
        defaultProjectId: newProjectId,
      },
      () => setStep(1),
    );
  }

  async function handleGitHubConnect() {
    if (!clerkUserId) return;
    const ok = await runStep(
      { clerkUserId, step: 1, scanCompleted: false },
      async () => {
        await github.connect();
      },
    );
    // Do not fire the OAuth redirect if we couldn't even persist the step;
    // the banner shows the error and Retry re-runs the full transition.
    if (!ok) return;
  }

  async function handleExistingGitHubInstall() {
    if (!clerkUserId) return;
    const ok = await runStep(
      { clerkUserId, step: 1, scanCompleted: false },
      async () => {
        await github.authorizeExistingInstallation();
      },
    );
    if (!ok) return;
  }

  async function handleGitHubSkip() {
    if (!clerkUserId) return;
    // Drop any previously-selected repo since the skip path commits the
    // user to the no-repo branch — otherwise stale state could reappear
    // if they navigate back to step 2 later.
    setSelectedRepo(null);
    const ok = await runStep(
      {
        clerkUserId,
        step: 4,
        scanCompleted: false,
        selectedRepo: '',
      },
      () => setStep(4),
    );
    if (!ok) return;
    await completeAndNavigate({
      clerkUserId,
      orgSlug: existingOrg?.slug ?? null,
      completionPath: 'deferred',
    });
  }

  async function handleRepoSelect(repoFullName: string) {
    setSelectedRepo(repoFullName);
    if (!clerkUserId) return;
    // Persist the selection immediately so a refresh at step 2 resumes
    // with the dropdown pre-filled instead of blank.
    const persist = runStep({
      clerkUserId,
      step: 2,
      selectedRepo: repoFullName,
    });
    repoSelectionPersistRef.current = persist;
    await persist.finally(() => {
      if (repoSelectionPersistRef.current === persist) {
        repoSelectionPersistRef.current = null;
      }
    });
  }

  async function handleRepoConfirm() {
    if (!clerkUserId || !selectedRepo || !projectId) return;
    const persistedSelection = repoSelectionPersistRef.current;
    if (persistedSelection) {
      const ok = await persistedSelection;
      if (!ok) return;
    }

    const ok = await runStep(
      {
        clerkUserId,
        step: 3,
        scanCompleted: false,
        selectedRepo,
      },
      () => setStep(3),
    );
    if (!ok) return;
  }

  async function handleScanComplete(completion: ScanCompletion) {
    if (!clerkUserId) return false;
    // Silent: StepScan renders its own handoff-failed panel (with retry) when
    // this returns false, so the global banner must stay quiet here.
    // Both the scanned and skipped paths land on Agent setup (step 4); only
    // the scanCompleted flag differs.
    return runStep(
      {
        clerkUserId,
        step: 4,
        scanCompleted: completion.status !== 'skipped',
      },
      () => setStep(4),
      { silent: true },
    );
  }

  async function handleReadyContinue(
    completionPath: OnboardingCompletionPath,
  ) {
    if (!clerkUserId) return;
    await completeAndNavigate({
      clerkUserId,
      orgSlug: existingOrg?.slug ?? null,
      completionPath,
    });
  }

  async function persistStep(stepToPersist: number): Promise<boolean> {
    if (!clerkUserId) return false;
    await startOnboarding({ clerkUserId });
    return runStep({ clerkUserId, step: stepToPersist });
  }

  async function back() {
    const previousStep = getPreviousOnboardingStep(step, Boolean(selectedRepo));

    const ok = await persistStep(previousStep);
    if (ok) setStep(previousStep);
  }

  async function handleScanBack() {
    const ok = await persistStep(2);
    if (ok) setStep(2);
  }

  // ── Loading state ─────────────────────────────────────────────

  if (
    !isLoaded ||
    !user ||
    (clerkUserId && (onboarding === undefined || memberships === undefined))
  ) {
    return (
      <div className={s.loadingLayout}>
        <FragmentsLogo size={48} className={s.spinning} />
      </div>
    );
  }

  // ── Step header ──────────────────────────────────────────────
  // Five-step wizard: Organisation, GitHub, Repository, Scan, terminal handoff.

  const stepNames = [
    "Let's get started",
    'Connect GitHub',
    'Choose repository',
    'Scan',
    'Agent setup',
  ] as const;

  const displayOrgName = orgForm.name.trim() || existingOrg?.name || '';

  const errorBanner = mutationError ? (
    <OnboardingErrorBanner
      message={mutationError}
      onRetry={pendingRetry}
      onDismiss={() => {
        setMutationError(null);
        setPendingRetry(null);
      }}
    />
  ) : null;

  return (
    <OnboardingShell
      step={step}
      stepNames={stepNames}
      showBack={step > 0 && step < 4}
      onBack={() => void back()}
      errorBanner={errorBanner}
    >
      {step === 0 && (
        <StepOrganisation
          onNext={handleOrgContinue}
          orgName={displayOrgName}
          onOrgNameChange={orgForm.setName}
          isSubmitting={orgForm.isSubmitting}
          error={orgForm.error}
          source="default"
        />
      )}
      {step === 1 && (
        <StepConnectGitHub
          onConnect={handleGitHubConnect}
          onUseExistingInstallation={handleExistingGitHubInstall}
          onSkip={handleGitHubSkip}
          isConnecting={github.isConnecting}
          error={github.error}
        />
      )}
      {step === 2 && orgId && (
        <StepSelectRepo
          onNext={handleRepoConfirm}
          selected={selectedRepo}
          onSelect={handleRepoSelect}
          appInstallUrl={github.appInstallUrl}
          onReconnect={handleGitHubConnect}
          isReconnecting={github.isConnecting}
          orgId={orgId}
        />
      )}
      {step === 3 && orgId && projectId && selectedRepo && (
        <StepScan
          orgId={orgId}
          projectId={projectId}
          selectedRepo={selectedRepo}
          onBack={() => void handleScanBack()}
          onComplete={handleScanComplete}
        />
      )}
      {step === 4 && orgId && (
        <StepReady
          orgId={orgId}
          projectId={projectId}
          repoFullName={selectedRepo}
          onBack={() => void back()}
          onContinue={handleReadyContinue}
        />
      )}
    </OnboardingShell>
  );
}
